import { Block } from '../block';
import { Bounding } from '../../../core/models/bounding';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../equipment-base';
import { EquipmentElements } from '../../../equipment/models/elements/equipmentElements';
import { Extension } from '../../../equipment/models/elements/extension';
import { Figure } from '../../../equipment/models/figures/figure';
import { GlobalSettings } from '../../../core/models/globalSettings';
import { ICanPutProductsResult } from '../canPutProductsResult';
import { Point } from '../../../core/models/point';
import { ProductErrors, ProductGroup } from '../product-group';
import { ProductGroupRotation } from '../product-group-rotation';

import { Utils } from '../../../core/services/utils.service';

export class HookElement extends Element {

	get anchorYCoordinate(): number {
		return this.shapes[0].begin.y;
	}

	get layoutBoundingX(): Bounding {
		const bounding: Bounding = this.getBoundingX();

		return {
			min: Utils.round(bounding.max - bounding.min),
			max: Utils.round(bounding.max - bounding.min),
		}
	}

	get layoutBoundingY(): Bounding {
		const bounding: Bounding = this.getBoundingY();

		return {
			min: bounding.min,
			max: bounding.max
		}
	}

	get layoutBoundingZ(): Bounding {
		const bounding: Bounding = this.getBoundingZ();

		return {
			min: bounding.max,
			max: bounding.max
		}
	}

	get products(): Array<Array<ProductGroup>> {
		const products: Array<ProductGroup> = this.stand.products
			.filter((pg: ProductGroup) => pg.element == this)
			.sort((a: ProductGroup, b: ProductGroup) => b.begin.z - a.begin.z || -1);

		return products.length ? [products] : [];
	}

	get sortedProducts(): Array<ProductGroup> {
		return super.sortedProducts.sort((a: ProductGroup, b: ProductGroup) => {
			return b.begin.z - a.begin.z;
		});
	}

	constructor(element: Element) {
		super(element.parent, element.shapes, element.extensions, element.depend, element.id, element.idx);
	}

	public addProduct(pg: ProductGroup) {
		pg.element = this;

		let hangerDistance: number = 0;

		if (this.depend?.parent.is(EquipmentElements.HangBar)) hangerDistance = 0.08;

		pg.placementHeight = 1;
		pg.angle = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
		pg.begin = {
			x: Utils.round(this.getBoundingX().min - (pg.ItemWidth - this.shapes[0].width) / 2),
			y: Utils.round(this.getBoundingY().min - pg.ItemHeight - hangerDistance),
			z: this.getBoundingZ().max
		};
	}

	canDrop(product: ProductGroup): boolean {
		return !product.isLooseForm;
	}

	copy(): HookElement {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy()),
			element: Element = new Element(this.parent, shapes, extensions, this.depend, this.id, this.idx);

		return new HookElement(element);
	}

	normalize(stand: EquipmentBase): void {
		const products: Array<Array<ProductGroup>> = this.products;

		if (!products.length) return;

		let beginZ: number = this.getBoundingZ().max;
		let hangerDistance: number = 0;

		if (this.depend?.parent.is(EquipmentElements.HangBar)) hangerDistance = 0.08;

		for (let product of products[0]) {
			product.begin.z = beginZ;
			product.begin.x = Utils.round(product.element.shapes[0].begin.x - (product.ItemWidth - product.element.shapes[0].width) / 2);
			product.begin.y = Utils.round(product.element.getBoundingY().min - product.ItemHeight - hangerDistance);
			beginZ = Utils.round(beginZ - product.Depth);

			product.error = false;

			const errors: { [key in ProductErrors]?: boolean } = {
				[ProductErrors.HookDepth]: beginZ < this.getBoundingZ().min,
				[ProductErrors.LooseOnShelf]: product.isLooseForm
			};

			product.error = Object.values(errors).some((error: boolean) => error);
			product.errorTypes = Object.keys(errors).filter((key: ProductErrors) => errors[key]);
		}

		if (products[0].some((product: ProductGroup) => product.error)) {
			for (let product of products[0]) {
				product.error = true;
			}
		}
	}

	public canPutProductGroups(products: Array<ProductGroup>, point: Point): ICanPutProductsResult {
		if (products.some((pg: ProductGroup) => pg.isLooseForm)) {
			return {
				success: false,
				message: {
					messageText: 'WEIGHT_PRODUCT_ON_SHELF_ERROR',
					translateParams: {}
				}
			};
		}

		const block: Block = this.stand.findBlock(point.x, point.y, products[0].Width);

		for (let product of products) {
			if (block && (product.block != block.id || !block.id)) {
				if (!block.isProductSuitable(product.product)) {
					return {
						success: false,
						message: {
							messageText: 'BLOCK_PRODUCT_ERROR',
							translateParams: { blockName: block.name }
						}
					};
				}
			}
		}

		return {
			success: true,
			message: {
				messageText: '',
				translateParams: {}
			}
		}
	}

	public putProductGroups(products: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): void {
		products.forEach((pg: ProductGroup, index: number) => {
			const autoDepth: boolean = pg.autoDepth;

			pg.dependProduct = null;

			if (pg.element != this) {
				pg.Element = this;
				pg.begin.z = index ? products[index - 1].getBoundingZ().min : this.getBoundingZ().max;

				if (products.length > 1) {
					pg.placementDepth = 1;
					pg.qty = 1;
				} else {
					const maxPlacementDepth: number = this.getMaxPlacementDepth(pg);

					if (autoDepth || maxPlacementDepth < pg.placementDepth) pg.placementDepth = maxPlacementDepth;

					pg.qty = pg.placementDepth;
				}
			} else {
				pg.begin.z = index ? products[index - 1].getBoundingZ().min : point.z;
			}

			this.stand.moveProductToTheEnd(pg);
		});
	}
}
